import { type Dispatch, type FC, type ReactNode, Fragment, lazy, Suspense, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { createSelector } from '@reduxjs/toolkit';

// @ts-expect-error - old hook form - replace with `react-hook-form@latest`
import { useForm } from 'react-hook-form';
import { ErrorMessage } from '@hookform/error-message';
// @ts-expect-error - old handsontable - replace with `@handsontable/react@12.4.0`
import type HotTable from '@handsontable/react';
import type { ChangeSource } from 'handsontable/common';

import { _get, _isEmpty, _isNotEmpty, _notNil } from '@/littledash';

import type { MetadataField } from '@/model/Metadata.model';
import { MetadataTypes } from '@/model/Metadata.model';
import type { Study, StudyMetrics } from '@/model/Study.model';

import { preventNumberScroll } from '@/helpers';
import { featuresSelector } from '@/support/Selectors';
import { RouteService } from '@/support/RouteService';

import Loading from '@/components/Loading';
import Button from '@/components/UI/Button';
import type { CustomGridSettings } from '@/components/UI/SpreadSheet/SpreadSheet.model';
import type { HotTableRef } from '@/components/UI/SpreadSheet/HotTableV16';

import { getMetaData } from '../Colony.utils';

/**
 * Lazy-loaded table components - isolated to prevent cross-version interference.
 */
const CagesTableV12 = lazy(() => import('@/components/UI/SpreadSheet'));
const CagesTableV16 = lazy(() => import('./CagesTableV16'));

const hotUpgradeSelector = createSelector([featuresSelector], (features) => ({
  hotUpgrade: features?.hot_upgrade ?? false,
}));

const newOverlay: ReactNode = (
  <>
    <h3 className="mb2">Cages and metadata will appear here</h3>
    <p style={{ maxWidth: '520px', alignSelf: 'center' }}>
      Enter the number of cages you will be adding in the text box above. You can continue to add additional cages the
      same way afterwards.
    </p>
  </>
);
const MAX_CAGES = 100;

export interface CagesState {
  cages: {
    settings: CustomGridSettings;
    data: Array<Record<string, unknown>>;
    metadata: Array<MetadataField>;
    metaGlossaries: Array<MetadataField>;
    cellErrors: Record<string, string>;
    overlay: ReactNode;
    cageCount: number;
    maxRows: number;
  };
}

export interface CagesAction {
  type: string;
  id?: string;
  data?: Record<string, unknown> | string;
}

type StudyWithCounts = Study & Partial<StudyMetrics>;

interface CagesProps {
  state: CagesState;
  dispatch: Dispatch<CagesAction>;
  study?: StudyWithCounts;
}

const Cages: FC<CagesProps> = ({ state, dispatch, study }) => {
  const {
    cages: { settings, data: cageData, metadata, cellErrors, overlay, cageCount, maxRows },
  } = state;

  const { hotUpgrade } = useSelector(hotUpgradeSelector);

  const [isInvalid, setIsInvalid] = useState<boolean>(false);
  const ref = useRef<HotTable | HotTableRef>(null);

  const { register, handleSubmit, errors, setError } = useForm({});

  // --- dispatch helpers ---

  const updateMetadata = (updatedMetadata: Array<MetadataField>): void => {
    dispatch({
      type: 'update',
      id: 'cages',
      data: { metadata: updatedMetadata },
    });
  };
  const updateGlossaries = (metaGlossaries: Array<MetadataField>): void => {
    dispatch({
      type: 'update',
      id: 'cages',
      data: { metaGlossaries },
    });
  };
  const updateOverlay = (updatedOverlay: ReactNode): void => {
    dispatch({
      type: 'update',
      id: 'cages',
      data: { overlay: updatedOverlay },
    });
  };
  const updateApiError = (apiError: unknown): void => {
    dispatch({
      type: 'update',
      id: 'cages',
      data: apiError as Record<string, unknown>,
    });
  };
  const updateMaxRows = (updatedMaxRows: number): void => {
    dispatch({
      type: 'update',
      id: 'cages',
      data: { maxRows: updatedMaxRows },
    });
  };
  const updateCellErrors = (updatedCellErrors: Record<string, string>): void => {
    dispatch({
      type: 'update',
      id: 'cages',
      data: { cellErrors: updatedCellErrors },
    });
  };
  const updateCageCount = (updatedCageCount: number): void => {
    dispatch({
      type: 'update',
      id: 'cages',
      data: { cageCount: updatedCageCount },
    });
  };

  const applyInvalidError = (currentErrors: Record<string, string>): void =>
    updateCellErrors({ ...currentErrors, 'auto-validation': 'There are invalid cells' });

  // --- callbacks ---

  const onSubmit = (rowAmountToAdd: Record<string, string>): false | void => {
    if (
      _notNil(_get(rowAmountToAdd, 'cage_amount')) &&
      cageData.length + Number(rowAmountToAdd.cage_amount) > MAX_CAGES
    ) {
      setError('cage_amount', {
        type: 'manual',
        message: `Cannot add more than ${MAX_CAGES} cages`,
      });
      return false;
    }
    updateMaxRows(Number(maxRows) + Number(rowAmountToAdd.cage_amount));
  };

  const afterContextMenuHide = (): void => {
    if (maxRows >= cageData.length) {
      updateMaxRows(cageData.length);
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const afterRemoveRow = (_source?: ChangeSource | undefined): void => {
    if (_isEmpty(cageData)) {
      updateOverlay(newOverlay);
    }
    const hot = ref.current?.hotInstance;
    const sourceData = hot?.getSourceData();

    if (_isEmpty(sourceData)) {
      updateCellErrors({ 'no-cage': 'You must supply at least 1 cage' });
    }
  };

  // --- effects ---

  useEffect(() => {
    getMetaData(MetadataTypes.collection_meta)
      .then((response) => {
        const data = response?.body ?? [];
        updateGlossaries(data as unknown as Array<MetadataField>);
        if (_notNil(settings?.columns)) {
          const metadataColumnIds = settings.columns.reduce<Array<unknown>>((acc, c) => {
            if (_notNil(c.metaID)) {
              acc.push(c.metaID);
            }
            return acc;
          }, []);
          const updatedMetadata = (data as unknown as Array<MetadataField>).filter(
            (d) => !metadataColumnIds.includes(d.id)
          );
          dispatch({ type: 'stepReady' });
          return updateMetadata(updatedMetadata);
        }
        dispatch({ type: 'stepReady' });
        return updateMetadata(data as unknown as Array<MetadataField>);
      })
      .catch((error: unknown) => updateApiError(error));
  }, []);

  useEffect(() => {
    if (_isEmpty(cageData)) {
      updateOverlay(newOverlay);
    }
  }, [cageData]);

  useEffect(() => {
    if (maxRows > 0 && maxRows > cageData.length) {
      const { 'no-cage': _noCage, ...remainingErrors } = cellErrors;
      void _noCage;
      if (isInvalid) {
        applyInvalidError(remainingErrors);
      } else {
        updateCellErrors(remainingErrors);
      }
      updateOverlay('');
      const hot = ref.current?.hotInstance;

      let cageNameNumber = cageData.length !== 0 ? cageCount : 1;

      if (_isEmpty(cageData) && _notNil(study) && _notNil(study.counts?.cages_with_trashed)) {
        cageNameNumber += study.counts!.cages_with_trashed;
      }

      const rowAmountToAdd = maxRows - cageData.length;

      if (hotUpgrade) {
        // v16: use 'insert_row_below' and batch all operations to prevent
        // validation from accessing cells before they are rendered.
        const insertIndex = cageData.length > 0 ? cageData.length - 1 : 0;
        hot?.batch(() => {
          hot.alter('insert_row_below', insertIndex, rowAmountToAdd, 'insertRows');
          const totalRows = hot.countRows();
          const startRow = Math.max(0, totalRows - rowAmountToAdd);
          const allChanges: Array<[number, number, number | string]> = [];
          let currentRow = startRow;
          let batchCageName = cageNameNumber;
          for (let i = 0; i < rowAmountToAdd; i++) {
            allChanges.push([currentRow, 0, batchCageName]);
            allChanges.push([currentRow, 1, `Cage ${batchCageName}`]);
            currentRow++;
            batchCageName++;
          }
          hot.setDataAtCell(allChanges, 'setCageID');
          cageNameNumber = batchCageName;
        });
      } else {
        // v12: original insert_row behaviour
        hot?.alter('insert_row', cageData.length + 1, rowAmountToAdd, 'insertRows');
        let newCageID = cageData.length - Number(rowAmountToAdd) + 1;
        for (let i = 0; i < Number(rowAmountToAdd); i++) {
          hot?.setDataAtCell(
            [
              [newCageID - 1, 0, cageNameNumber],
              [newCageID - 1, 1, `Cage ${cageNameNumber}`],
            ],
            `${newCageID === cageData.length ? 'setLastCageID' : 'setCageID'}`
          );
          newCageID++;
          cageNameNumber++;
        }
      }
      updateCageCount(cageNameNumber);
    }
  }, [maxRows]);

  useEffect(() => {
    if (isInvalid) {
      applyInvalidError(cellErrors);
    } else {
      const { 'auto-validation': _autoVal, ...remainingErrors } = cellErrors;
      void _autoVal;
      updateCellErrors(remainingErrors);
    }
  }, [isInvalid]);

  // --- render ---

  const TableComponent = (
    <Suspense fallback={<Loading />}>
      <div className="ow-spreadsheet-styles">
        {hotUpgrade ? (
          <CagesTableV16
            data={cageData}
            innerRef={ref as React.RefObject<HotTableRef>}
            metadata={metadata}
            setMetadata={updateMetadata}
            setInvalid={setIsInvalid}
            overlay={overlay}
            maxRows={maxRows}
            afterRemoveRow={afterRemoveRow}
            afterContextMenuHide={afterContextMenuHide}
          />
        ) : (
          _isNotEmpty(settings) && (
            <CagesTableV12
              data={cageData}
              settings={settings}
              innerRef={ref as React.RefObject<HotTable>}
              className="cageCreation"
              metadata={metadata}
              setMetadata={updateMetadata}
              buttonClasses={'absolute-sheet-buttons'}
              overlay={overlay}
              afterRemoveRow={afterRemoveRow}
              maxRows={maxRows}
              afterContextMenuHide={afterContextMenuHide}
              setInvalid={setIsInvalid}
            />
          )
        )}
      </div>
    </Suspense>
  );

  return (
    <Fragment>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="flex-row items-start flex flex-wrap mb4">
          <p className="lh-copy set-width">
            <span>Enter the amount of cages, animals per cage and any metadata to continue. </span>
            <a
              href={RouteService.help('registering_animals')}
              target="_blank"
              rel="noopener noreferrer"
              className="dib link blue"
            >
              Read more.
            </a>
          </p>
        </div>
        <span className="near-black">How many more cages would you like to add?</span>
        <div className="flex db mt2 w2">
          <input
            type="number"
            onWheel={preventNumberScroll}
            name="cage_amount"
            placeholder="5"
            defaultValue="5"
            ref={register({
              required: 'You must supply an numrical amount of cages to create.',
              pattern: {
                value: '/[0-9]*/i',
                message: 'Please only input numerical values.',
              },
              max: {
                value: `${MAX_CAGES}`,
                message: `You have exceeded the maximum cage limit of ${MAX_CAGES}.`,
              },
            })}
          />
          <Button submit className="ml3" disabled={_isNotEmpty(errors)} plain>
            Add Cages
          </Button>
        </div>
        <ErrorMessage
          errors={errors}
          name="cage_amount"
          render={({ message }) => <p className="f6 red db">{message}</p>}
        />
      </form>

      {TableComponent}
    </Fragment>
  );
};
export default Cages;
